import { useMutation } from "@tanstack/react-query"
import clsx from "clsx"
import Cookies from "js-cookie"
import { useState } from "react"

import useRouter from "@hooks/useRouter"
import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"
import { ifSpaceOrEnter } from "@utils/keyboard"

import { TruncatableText } from "@particles/index"

import { Icon } from "@atoms/index"

import { CompanyAvatar, Select } from "@molecules"
import { GenericError } from "@molecules/Form/GenericForm/GenericForm.types"
import { SelectListItem } from "@molecules/Select/SelectListItem/SelectListItem.types"
import toast from "@molecules/Toast/Toast"

import { ROOT_ROUTE } from "@routes/root"

import { SERVICE_COMPANY } from "@endpoints/serviceCompany"

import styles from "./NavSidebarWorkspaceButton.module.scss"
import { NavSidebarWorkspaceButtonProps, SwitchServiceCompanyError } from "./NavSidebarWorkspaceButton.types"

export default function NavSidebarWorkspaceButton(props: NavSidebarWorkspaceButtonProps) {
    const { _isFocused, _isHovered, isPressed, isDisabled, defaultOpen = false } = props

    const { user } = useUser()
    const router = useRouter()
    const [isOpen, setIsOpen] = useState(defaultOpen)

    const switchServiceCompanyMutation = useMutation<void, GenericError<SwitchServiceCompanyError>, string>({
        mutationFn: async (serviceCompanyId: string): Promise<void> => {
            const response = await fetch(SERVICE_COMPANY.SWITCH_SERVICE_COMPANY, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": Cookies.get("csrftoken") ?? "",
                },
                body: JSON.stringify({ service_company_id: serviceCompanyId }),
            })

            if (!response.ok) {
                await response.json().then((error: SwitchServiceCompanyError) => {
                    throw new GenericError("Failed to switch service company", error)
                })
            }
        },
        onSuccess: () => {
            router.push(ROOT_ROUTE)
        },
        onError: (error) => {
            toast({
                variant: "default",
                title: error.error.detail,
                type: "error",
            })
        },
    })
    const handleSelect = (item: SelectListItem) => {
        const serviceCompanyId = item.value
        switchServiceCompanyMutation.mutate(serviceCompanyId)
    }

    if (user) {
        const items: SelectListItem[] = user.accessible_service_companies.map((company) => ({
            value: String(company.id),
            label: company.name,
            leading: <CompanyAvatar serviceCompany={company} size="xs" />,
        }))

        const hasMultipleCompanies = items.length > 1

        if (hasMultipleCompanies) {
            return (
                <div className={styles.switchable}>
                    <Select.Root isOpen={isOpen} onOpenChange={setIsOpen}>
                        <Select.Trigger>
                            <div
                                role="button"
                                tabIndex={isDisabled ? -1 : 0}
                                className={clsx(styles.base, {
                                    [styles.isFocused]: _isFocused,
                                    [styles.isHovered]: _isHovered,
                                    [styles.isPressed]: isPressed || isOpen,
                                    [styles.isDisabled]: isDisabled,
                                })}
                                onKeyDown={(e) => ifSpaceOrEnter(e, () => setIsOpen(!isOpen))}
                            >
                                <div className={styles.logo}>
                                    <CompanyAvatar serviceCompany={user.service_company} size="md" />
                                </div>
                                <TruncatableText
                                    text={user.service_company.name}
                                    className={styles.name}
                                    tooltipOnTruncate={true}
                                />
                                <Icon
                                    icon={faIcon({ name: "angles-up-down", style: "regular" })}
                                    container="fixedHeight"
                                    size={14}
                                    containerClassName={styles.arrow}
                                />
                            </div>
                        </Select.Trigger>
                        <Select.Viewport side="bottom" align="end">
                            <Select.List title="Workspace" className={styles.list}>
                                <Select.ListGroup>
                                    {items.map((item) => (
                                        <Select.ListItem
                                            variant="default"
                                            key={item.value}
                                            item={item}
                                            onSelect={handleSelect}
                                            isSelected={item.value === String(user.service_company.id)}
                                        />
                                    ))}
                                </Select.ListGroup>
                            </Select.List>
                        </Select.Viewport>
                    </Select.Root>
                </div>
            )
        } else {
            return (
                <div className={styles.base}>
                    <div className={styles.logo}>
                        <CompanyAvatar serviceCompany={user.service_company} size="md" />
                    </div>
                    <TruncatableText
                        text={user.service_company.name}
                        className={styles.name}
                        tooltipOnTruncate={true}
                    />
                </div>
            )
        }
    } else {
        return null
    }
}
